import {type RecordOf} from 'immutable';
import {type BitmaskValue} from '@packages/utilities/fp/data/bitmask';
import {Plugins} from '@packages/systems/wfdl';
import type {LayoutMatrix} from '@packages/systems/layout';
import {type EditMultipleInstancesConfirmModalMetadataType} from '@packages/systems/designer/components';
import type {
  SettingsPanelSettingType,
  CommerceSettingsPanelSettingType,
} from '@packages/systems/designer/settings';
import type {NativeId} from '@packages/systems/core/utils/NativeIdUtils';
import type {AppearanceSettings} from '@packages/shared/types/appearanceSettings';
import type {
  DarkThemeStyle,
  GlobalThemeSelection,
  OSThemePreference,
  ThemeSurfaceOverrides,
} from '@packages/shared/types/globalInterfaceTheme';
import {type LibrariesModalState} from '@packages/systems/libraries';

// Interaction version types
export type IxVersion = 'ix1' | 'ix2' | 'ix3';

export const ON_CANVAS_CONTROL_PANELS = {
  FLEX: 'flex-panel' as const,
  LAYOUT: 'layout-panel' as const,
} as const;

export type MergeCellWarningModalMetadataType = {
  layoutElementId: string;
  targetLayoutMatrix: LayoutMatrix;
  location: string;
};

export type OutlineState = BitmaskValue;
export enum OutlinePositionMode {
  /**
   * Applies to elements in `fixed` position. Changes to Canvas scroll position do not change the original computed bounds
   */
  Fixed = 'fixed',
  /**
   * Applies to elements in `sticky` position. Any changes to Canvas scroll position will force recomputation of bounds.
   */
  Sticky = 'sticky',
  /**
   * Applied to all other element positioning rules. This is the default.
   * Changes to Canvas scroll position are tracked and applied to the element bounds. Forced recompuation only takes place when needed
   */
  Tracked = 'tracked',
}

const hasPositionMode = (
  element: HTMLElement,
  positionMode: 'fixed' | 'sticky'
): boolean => {
  let parent = element;
  do {
    const style = window.getComputedStyle(parent);
    if (style.position === positionMode) {
      return true;
    }
    // @ts-expect-error - TS2322 - Type 'Element | null' is not assignable to type 'HTMLElement'.
  } while ((parent = parent.offsetParent));

  return false;
};

export const getOutlinePositionMode = (
  node: HTMLElement
): OutlinePositionMode => {
  if (hasPositionMode(node, 'fixed')) return OutlinePositionMode.Fixed;
  if (hasPositionMode(node, 'sticky')) return OutlinePositionMode.Sticky;
  return OutlinePositionMode.Tracked;
};

type TipsGridType = 'none' | 'basic' | 'filled';
type RulersType = 'no-ruler' | 'ruler-pinned' | 'ruler-pinned-crosshair';
export type OnCanvasSpacingMode = 'off' | 'audit';

export {
  MiniToolbarButtons,
  MiniToolbarInstanceProxyContext,
  useMiniToolbarInstanceProxy,
  type MiniToolbarButtonsType,
  type MiniToolbarMetadata,
  type SliderNavigationMetadata,
  PENDING_EDIT_TYPE,
  PENDING_EDIT_ACTION_CLEARED,
  clearPendingEditAction,
  selectPendingMiniToolbarAction,
  type PendingMiniToolbarAction,
  type PendingDoubleClickAction,
  type PendingEditAction,
  PENDING_MINI_TOOLBAR_ACTION_SET,
  PENDING_DOUBLE_CLICK_ACTION_SET,
  setPendingMiniToolbarAction,
  setPendingDoubleClickAction,
  type FocusThenEditAction,
} from './miniToolbar';

export type LeftSidebarTipsTypeShape = {
  edges: boolean;
  empty: boolean;
  parentOutline: boolean;
  grid: TipsGridType;
  xray: boolean;
  cssPreview: boolean;
  rulers: RulersType;
  onCanvasSpacing: OnCanvasSpacingMode;
};

export type OnCanvasControlsPanelType =
  (typeof ON_CANVAS_CONTROL_PANELS)[keyof typeof ON_CANVAS_CONTROL_PANELS];

export type UiStateProps = {
  // OPEN_UI_DEFAULTS
  asset: string | null | undefined;
  addPanelSearchQuery: string;
  assetDeleteConfirmation: boolean;
  pseudoClassesVisible: boolean;
  // State
  selectedNodeNativeId: string | null | undefined;
  designSystemInspectorDialogVisible: boolean;
  navigatorRefactorVisible: boolean;
  keyboardShortcutsVisible: boolean;
  themePreferencesModalVisible: boolean;
  themesAnnouncementModalVisible: boolean;
  createBranchModalVisible: boolean;
  siteBranchManagerModalVisible: boolean;
  siteBranchManagerInitialSiteBranchId: string | null;
  legacyGuestConversionPickerVisible: boolean;
  helpModalVisible: boolean;
  sendFeedbackModalVisible: boolean;
  confirmFeedbackModalVisible: boolean;
  identityCaptureModalVisible: boolean;
  responsiveImageMigrationOverlayVisible: boolean;
  leftSidebarHelp: Plugins.QualifiedElementPreset | null | undefined;
  leftSidebarTips: RecordOf<LeftSidebarTipsTypeShape> | null | undefined;
  leftSidebarScreenshotTooltip:
    | Plugins.QualifiedElementPreset
    | null
    | undefined;
  canvasBarPosition: CanvasBarPosition;
  canvasBarPositionIAIterations: CanvasBarPosition;
  canvasBarRulerVisible: boolean;
  lastIdle: number;
  contextDropdownOpen: boolean;
  isFirstTimePreviewModeToggle: boolean;
  topBarSetToBeHidden: boolean;
  uiHidden: boolean;
  topBarHovered: boolean;
  publishDropdownOpen: boolean;
  miniSettingsFlags: {
    [key: string]: boolean;
  };
  atomsIframeLoaded: boolean;
  workspaceLoaded: boolean;
  selectedIxVersion: IxVersion | undefined;
  ix2KeyboardContext: 'CANVAS' | 'IX2_TIMELINE';
  settingsPanelSetting: SettingsPanelSettingType | null | undefined;
  commercePanelSetting: CommerceSettingsPanelSettingType | null;
  srcsetOptions: boolean;
  settingSelectPending: SettingsPanelSettingType | null | undefined;
  settingCommerceSelectPending:
    | CommerceSettingsPanelSettingType
    | null
    | undefined;
  commerceSettingsClosePending: boolean;
  isNexusAddressModalOpen: boolean;
  removeAddressRequested: boolean;
  isStoreSetupModalOpen: boolean;
  ix2IsNewActionList: boolean;
  isRestoreVersionModalOpen: boolean;
  isCorruptVariantsModalOpen: boolean;
  isColorContrastLineVisible: boolean;
  appliedColorFilter: string;
  isPageLimitsBreakdownVisible: boolean;
  textZoom: number;
  onCanvasControlsPanelOpen: {
    [key: string]: boolean;
  } | null;
  highlightSelectedNode: boolean;
  dismissedLayoutPresetPopovers: Set<string>;
  mergeCellWarningModalMetadata:
    | MergeCellWarningModalMetadataType
    | null
    | undefined;
  hasDismissedMergeCellWarning: boolean;
  isEditMultipleInstancesConfirmModalOpen: boolean;
  editMultipleInstancesConfirmModalMetadata: EditMultipleInstancesConfirmModalMetadataType;
  isCanvasResizePopoverOpen: boolean;
  isCanvasResizePopoverAdvancedSettingsOpen: boolean;
  hasDraggedSpacingInSession: boolean;
  pendingSpacingHighlightFlash: boolean;
  librariesModal: LibrariesModalState;
  collectionFieldUsageModalOpen: boolean;

  /**
   * Indicates whether the user has enabled custom code preview for this site.
   * It has three possible values: `enabled`, `disabled`, and `unknown`.
   * `unknown` means the user has not toggled the setting yet. We do this because
   * this setting will fallback to the site level custom code preview setting
   * if the user has not toggled it yet.
   */
  isCustomCodePreviewEnabled: 'enabled' | 'disabled' | 'unknown';

  /**
   * Indicates whether the site has enabled custom code preview for this site.
   * This is the value that will be used if the user has not toggled the
   * `isCustomCodePreviewEnabled` setting yet.
   */
  isSiteCustomCodePreviewEnabled: boolean;

  isWxpTourModalOpen: boolean;
  isCanvasAppReady: boolean;
  canvasAppIframeLoaded: boolean;

  /**
   * Indicates whether the current site has completed AI Site Generation
   * onboarding. Always `undefined` for non-AISG sites.
   *
   * @privateRemarks
   *
   * Synced value from persistentUIState, via:
   * - `WebflowFlux`
   * - `PersistentStorageEngine`
   * - `aisgOnboardingCompleted`
   */
  hasCompletedAISiteGenOnboarding?: boolean;

  /**
   * Indicates if the page is currently loading.
   * Page loading state can be triggered by: switching pages, switching locales, switching to/from view/edit modes.
   */
  isPageLoading: boolean;

  /**
   * Indicates whether this is the first time the user is using custom code preview on this site.
   * Defaults to true for users on new sites. Set to false once the user uses preview or
   * commenting with custom code enabled on specific site. This flag is never reset back to true.
   */
  isFirstTimeCustomCodePreview?: boolean;
  isMultiplayerConnectionStatusToggled: boolean;

  cmsCollectionListPreviewEnabled: boolean; // The global setting for whether collection list preview is enabled
  cmsCollectionListPreviewItemsCount: number; // The global setting for the number of collection list items to preview
  cmsNestedCollectionListPreviewItemsCount: number; // The global setting for the number of nested collection list items to preview

  /**
   * Indicates which realtime refresh modal should be shown, if any.
   * - 'enabled': when legacy sync fails because realtime was enabled
   * - 'disabled': when multiplayer init fails because realtime was disabled
   * - null: no modal should be shown
   */
  realtimeRefreshModalType: null | 'enabled' | 'disabled';
  /**
   * Indicates which reload prompt originated by the multiplayer server should
   * be shown, if any. Rendered by MultiplayerServerNotificationModal.
   * - 'externalized-bindings-changed': the site's externalized localization
   *   bindings flag was toggled; the Designer reads it only at load
   * - null: no modal should be shown
   */
  multiplayerServerNotificationType: null | 'externalized-bindings-changed';
  showOrphanedLibraryComponents: boolean;
  landingPageOrionAppId: string | null | undefined;
  hasDismissedOrionWelcomeModal: boolean;
  orionLandingPageFTUCompletedTasks: string[];
  isCampaignReportingOpen: boolean;
};

export type UiStateType = RecordOf<UiStateProps>;

export type ElementBreadcrumbData = {
  type: 'element';
  icon: React.ReactNode;
  nativeId: NativeId | null;
  nativeIdPath: NativeId[];
  label: string;
};

export type VariantBreadcrumbData = {
  type: 'style-variant';
  variantId: string;
  label: string;
};

export type MCCFrameBreadcrumbData = {
  type: 'mcc-frame';
  frameId: string;
  label: string;
  isBase: boolean;
  isFrameSelected: boolean;
};

type MoreIconBreadcrumbData = {
  type: 'more';
};
export type BreadcrumbElementType =
  | ElementBreadcrumbData
  | VariantBreadcrumbData
  | MCCFrameBreadcrumbData
  | MoreIconBreadcrumbData;

export type TriggeredFrom = 'navigator' | 'canvas' | null;

export type CanvasContextMenuState = Readonly<{
  x: number;
  y: number;
  isVisible: boolean;
  breadcrumbs: Array<ElementBreadcrumbData>;
  /**
   * Indicates where the user triggered the Context Menu from before choosing some action.
   * Currently we use this value for Analytics, tracking events originating from the Context Menu if it was open either over the Navigator or Canvas.
   * The value is set to `null` when the Context Menu is closed.
   */
  triggeredFrom: TriggeredFrom;
}>;

export type SizeType = {
  width: number;
  height: number;
};

export type AppearanceSettingsState = {
  darkThemeStyle?: DarkThemeStyle;
  globalThemeSelection?: GlobalThemeSelection;
  osThemePreference?: OSThemePreference;
  selectedOption: AppearanceSettings;
  /**
   * Ephemeral announcement-modal preview. When set, theming consumers resolve
   * from this selection instead of global/overrides so preview can show without
   * persisting `globalThemeSelection`.
   */
  themeSelectionPreview?: GlobalThemeSelection;
  themeSurfaceOverrides?: ThemeSurfaceOverrides;
  /** Whether the user has explicitly changed a theme setting this session. */
  hasExplicitThemeAction?: boolean;
  /** Whether the user has explicitly changed the account-global theme selection in this session. */
  hasExplicitGlobalThemeSelectionAction?: boolean;
  /** Whether this session has edited overrides, rather than only hydrated them. */
  hasExplicitThemeSurfaceOverridesAction?: boolean;
};

export type CanvasBarPosition = 'top' | 'bottom';

export {default as ListHeader, type ListHeaderProps} from './ListHeader';
export {default as PropList} from './PropList';
export type {ItemType} from './PropList';
